import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ContentMetadata } from 'services/content/contentQueries';
import { RoomType } from 'providers/RoomRegistrar';
import { ApiProposalRepository } from 'services/proposal/repositories/implementations/ApiProposalRepository';
import { QueryKeys } from 'utils/queryKeys';
import { createMentionId } from '../utils/mentionUtils';
import { useContentMetadata } from 'components/editor/hooks/useContentMetadata';
import { ContentType } from 'utils/getContentType';
import type { User } from 'services/entities/PersonEntity';

interface UseMentionSuggestionsProps {
  isPrivate: boolean;
  contentId: string;
}

const filterUsersBySearch = (users: User[], searchTerm: string) => {
  if (!searchTerm) return users;
  const lowerSearchTerm = searchTerm.toLowerCase();
  return users.filter((user) => {
    const fullName = user.fullName?.toLowerCase() || '';
    const email = user.email?.toLowerCase() || '';
    return fullName.includes(lowerSearchTerm) || email.includes(lowerSearchTerm);
  });
};

const getUserIdsFromUsers = (users: User[]) => {
  return users.map((user) => createMentionId(user.id.toString(), user.email, user.fullName || user.email));
};

const useMentionSuggestionsPrivate = (enabled: boolean) => {
  const { data: accountUsers } = useQuery({
    queryKey: [QueryKeys.accountUsers],
    queryFn: ApiProposalRepository.getAccountUsers,
    enabled,
  });

  return useCallback(
    async ({ text }: { text: string; roomId: string }) => {
      if (!enabled || !accountUsers) return [];
      const filteredUsers = filterUsersBySearch(accountUsers, text);
      return getUserIdsFromUsers(filteredUsers);
    },
    [accountUsers, enabled]
  );
};

const getPublicMentionableUsers = (documentData: ContentMetadata | undefined) => {
  const users: Array<{ id: string; name?: string; email?: string }> = [];
  const addedKeys = new Set<string>();

  const addUser = (id: string, name?: string, email?: string) => {
    const key = email ? `${id}-${email}` : id;
    if (id && !addedKeys.has(key)) {
      addedKeys.add(key);
      users.push({ id, name, email });
    }
  };

  if (documentData?.assignedUser?.name) {
    addUser(documentData.assignedUser.id.toString(), documentData.assignedUser.name, documentData.assignedUser.name);
  }

  if (documentData?.prospect?.contact) {
    const contact = documentData.prospect.contact;
    const id = contact.id.toString();
    if (id) {
      addUser(id, contact.name, contact.email);
    }
  }

  return users;
};

const filterPublicUsersBySearch = (users: Array<{ id: string; name?: string; email?: string }>, searchTerm: string) => {
  if (!searchTerm) return users;
  const lowerSearchTerm = searchTerm.toLowerCase();
  return users.filter((user) => {
    const name = user.name?.toLowerCase() || '';
    const email = user.email?.toLowerCase() || '';
    return name.includes(lowerSearchTerm) || email.includes(lowerSearchTerm);
  });
};

const useMentionSuggestionsPublic = (enabled: boolean, contentId: string) => {
  const { data: documentData } = useContentMetadata(ContentType.DOCUMENT, contentId);

  return useCallback(
    async ({ text }: { text: string; roomId: string }) => {
      if (!documentData) return [];
      const mentionableUsers = getPublicMentionableUsers(documentData);
      const filteredUsers = filterPublicUsersBySearch(mentionableUsers, text);
      return filteredUsers.map((user) => {
        return createMentionId(user.id.toString(), user.email || '', user.name || user.email || '');
      });
    },
    [documentData]
  );
};

export const useMentionSuggestions = ({ isPrivate, contentId }: UseMentionSuggestionsProps) => {
  const privateCallback = useMentionSuggestionsPrivate(isPrivate);
  const publicCallback = useMentionSuggestionsPublic(!isPrivate, contentId);

  return useCallback(
    async ({ text, roomId }: { text: string; roomId: string }) => {
      const isPrivateRoom = roomId.includes(RoomType.PRIVATE);
      if (isPrivateRoom || isPrivate) {
        return privateCallback({ text, roomId });
      }
      return publicCallback({ text, roomId });
    },
    [isPrivate, privateCallback, publicCallback]
  );
};
